import {
  Component,
  computed,
  effect,
  inject,
  Input,
  OnInit,
  Output,
  EventEmitter,
  signal,
  viewChild,
  ElementRef,
  HostListener,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { MatIcon } from '@angular/material/icon';
import { Router, NavigationEnd } from '@angular/router';
import { I18nPipe } from '../../shared/i18n/i18n-pipe';
import { I18nScopedService } from '../../shared/i18n/i18n-scoped.service';
import { provideI18nScope } from '../../shared/i18n/i18n-scope.provider';
import { DashboardService } from '../../features/dashboard/services/dashboard/dashboard.service';
import { AddDashboardService } from '../dashboard/add-dashboard-dialog/add-dashboard.service';
import { firstValueFrom, filter, catchError, of } from 'rxjs';
import { I18nScopeReadyDirective } from '../../shared/i18n/i18n-scope-ready.directive';
import { ProjectService } from '../../shared/services/project/project.service';
import { DashboardListItem } from '../../features/dashboard/models/dashboard-list-item/dashboard-list-item.model';
import { MatTooltip } from '@angular/material/tooltip';
import { HasPermissionDirective } from '../../shared/directives/has-permission.directive';
import { HasSectionDirective } from '../../shared/directives/has-section.directive';
import { WEIGHBRIDGE_FILES } from '../../features/procurement/reference-file-view/weighbridge-files';
import {
  ACCESS_LEVEL,
  meetsAccessLevel,
  Permissions,
  REFERENCE_PARTY_SECTIONS,
  SECTIONS,
  Section,
  MotherDbCollectionDto,
  ReportListItemDto,
} from '@anessa/shared-models';
import { MotherdbApiService } from '../../features/motherdb/services/motherdb-api.service';
import { ReceptionsApiService } from '../../features/receptions/services/receptions-api.service';
import { ExpeditionsApiService } from '../../features/expeditions/services/expeditions-api.service';
import { NotificationDotTestService } from '../../shared/services/notification-dot-test/notification-dot-test.service';
import { TimeSeriesTablesService } from '../../features/timeseries-data/services/timeseries-tables.service';
import { ReportService } from '../../features/report-creation/services/report.service';
import { PermissionService } from '../../shared/services/permissions/permission.service';
import { I18nService } from '../../shared/i18n/i18n.service';
import {
  everyPlantSectionFor,
  isHiddenForMultiPlant,
} from '../../shared/nav/multi-plant-visibility';
import { TabViewService } from '../../shared/tab-view/tab-view.service';
import { isOpenInNewTabClick } from '../../shared/tab-view/tab-click.util';

interface NavItem {
  label: string;
  icon?: string;
  symbolIcon?: string;
  route: string;
  disabled?: boolean;
  viewPermission?: string;
  editPermission?: string;
  /** Section-level gate (leveled access). The item still renders, but disabled
   *  when the caller lacks `view` — see isNavItemDisabled. Used instead of the
   *  permission pair for section-only features (e.g. Suivi biologie). */
  section?: Section;
  /** When true, the item is disabled until a project is selected. Good to have
   * until we have the full project/plant flow figured out so the sidebar can handle it*/
  requiresProject?: boolean;
  /** Renders the item as a dropdown; `route` is then the prefix its children share. */
  children?: NavChild[];
}

interface NavChild {
  label: string;
  route: string;
  permission?: string;
  /** Section-level gate (leveled access) — used instead of `permission` for
   *  section-only features (e.g. Digestat). Checked via canViewSection. */
  section?: Section;
  isRawLabel?: boolean;
  translateLabel?: boolean;
  /** Coming-soon placeholder: rendered disabled, not gated, never navigates. */
  disabled?: boolean;
}

interface NavGroup {
  label: string;
  icon: string;
  children: NavChild[];
}

/** The MotherDB view listed as « BDD Intrants » (Catalogue intrants), not with the other views. */
const BDD_INTRANTS_VIEW = 'feedstock';

/**
 * The energy MotherDB views, in Données entrantes' « Energie » folder (CVES-12,
 * ADEV-1207). Curated: a new view is listed flat until it is added here.
 */
const ENERGIE_VIEWS: readonly string[] = [
  'gas_daily',
  'electricity_consumption_daily',
  'electricity_consumption_hourly',
];

@Component({
  selector: 'app-sidebar',
  imports: [
    MatIcon,
    I18nPipe,
    I18nScopeReadyDirective,
    MatTooltip,
    HasPermissionDirective,
    HasSectionDirective,
    NgTemplateOutlet,
    CdkDropList,
    CdkDrag,
  ],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.scss',
  providers: [provideI18nScope('ui/sidebar'), I18nScopedService],
})
export class Sidebar implements OnInit {
  private readonly router = inject(Router);
  private readonly dashboardService = inject(DashboardService);
  private readonly reportService = inject(ReportService);
  private readonly addDashboardService = inject(AddDashboardService);
  private readonly timeseriesTablesService = inject(TimeSeriesTablesService);
  private readonly motherdbApi = inject(MotherdbApiService);
  private readonly receptionsApi = inject(ReceptionsApiService);
  private readonly expeditionsApi = inject(ExpeditionsApiService);
  private readonly notificationDotTest = inject(NotificationDotTestService);
  private readonly i18n = inject(I18nService);
  private readonly permissionService = inject(PermissionService, {
    optional: true,
  });
  readonly projectService = inject(ProjectService);
  private readonly tabViewService = inject(TabViewService);
  readonly DASHBOARD_ROUTES = ['/dashboard', '/overview'];
  readonly REPORT_ROUTE = '/reports';
  readonly CUSTOM_VIEWS_ROUTE = '/custom-views';
  /**
   * Static boards nested under /dashboard. They share the prefix with the
   * user-created dashboards but are NOT dashboard ids, so the dashboard-id sync
   * and the Dashboards active-state both have to skip them.
   */
  readonly OPERATIONS_SITE_PREFIX = '/dashboard/operations-site';

  readonly dashboardsSection =
    viewChild<ElementRef<HTMLElement>>('dashboardsSection');
  readonly reportsSection =
    viewChild<ElementRef<HTMLElement>>('reportsSection');

  dashboardsExpanded = signal(false);
  reportsExpanded = signal(false);

  constructor() {
    // Listen to route changes to update active state,
    // this is necessary to keep the sidebar in sync when navigating in other components (ex: navigating to first dashboard on login)
    this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe((e) => {
        this.syncActiveStateFromUrl(e.urlAfterRedirects);
        void this.permissionService?.refreshPermissionsForActivePlant({
          force: true,
        });
      });

    effect(() => {
      const selectedDashboardID = this.dashboardService.selectedDashboardID();
      const activeRoute = this.activeRoute();
      if (!selectedDashboardID) return;
      if (!this.DASHBOARD_ROUTES.includes(activeRoute)) return;

      this.activeSubroute = selectedDashboardID;
    });

    // Load the MotherDB collection registry once the user is allowed to see it
    // (Données entrantes, the only group listing collections). Gating the fetch
    // avoids 403s (and poisoning the shared cache) for everyone else.
    effect(() => {
      const canView =
        this.permissionService?.canViewSection(SECTIONS.DONNEES_ENTRANTES) ?? false;
      if (!canView || this.motherdbFetched) return;
      this.motherdbFetched = true;
      this.motherdbApi
        .getCollections()
        .pipe(catchError(() => of([])))
        .subscribe((collections) => this.motherdbCollections.set(collections));
    });

    // Notification-dot counts: how many receptions/expeditions still lack their
    // on-site routing (destination / origin). Only fetched for edit-capable
    // roles — the dot nudges the people who can actually fix it. Refetches on
    // plant change (singlePlantId reads the currentProject signal).
    effect(() => {
      const plantId = this.projectService.singlePlantId;
      const canEdit =
        this.permissionService?.canEditSection(SECTIONS.APPROVISIONNEMENTS) ??
        false;
      if (!plantId || !canEdit) {
        this.missingDestinationCount.set(0);
        return;
      }
      this.receptionsApi
        .getMissingDestinationCount(plantId)
        .pipe(catchError(() => of(0)))
        .subscribe((count) => this.missingDestinationCount.set(count));
    });
    effect(() => {
      const plantId = this.projectService.singlePlantId;
      const canEdit =
        this.permissionService?.canEditSection(SECTIONS.EXPEDITIONS) ?? false;
      if (!plantId || !canEdit) {
        this.missingOriginCount.set(0);
        return;
      }
      this.expeditionsApi
        .getMissingOriginCount(plantId)
        .pipe(catchError(() => of(0)))
        .subscribe((count) => this.missingOriginCount.set(count));
    });

    this.initMotherDbLabels();
  }

  /** Receptions on the active plant still missing an on-site destination. */
  readonly missingDestinationCount = signal(0);
  /** Expeditions on the active plant still missing a stock origin. */
  readonly missingOriginCount = signal(0);

  toggleDashboardsSection(): void {
    if (this.collapsed) {
      this.openDashboardsSection();
    } else {
      this.dashboardsExpanded.update((expanded) => !expanded);
    }
  }

  toggleReportsSection(): void {
    if (this.collapsed) {
      if (this.reportsNavItem) {
        this.navigateTo(this.reportsNavItem);
      }
    } else {
      this.reportsExpanded.update((expanded) => !expanded);
    }
  }

  @HostListener('document:click', ['$event.target'])
  onDocumentClick(target: EventTarget | null): void {
    if (!(target instanceof Node)) {
      return;
    }

    if (this.dashboardsExpanded()) {
      const dashboardsEl = this.dashboardsSection()?.nativeElement;
      if (dashboardsEl && !dashboardsEl.contains(target)) {
        this.dashboardsExpanded.set(false);
      }
    }

    if (this.reportsExpanded()) {
      const reportsEl = this.reportsSection()?.nativeElement;
      if (reportsEl && !reportsEl.contains(target)) {
        this.reportsExpanded.set(false);
      }
    }
  }

  /**
   * Whether to show the "missing routing" notification dot on a nav item. The
   * counts are already gated to edit-capable roles (0 otherwise), so this is a
   * plain count check keyed on the item's route. The dev-menu test override
   * (NotificationDotTestService) wins when active, so QA can preview each state.
   */
  showNotificationDot(item: NavItem): boolean {
    if (item.route === '/approvisionnements') {
      return (
        this.notificationDotTest.override('approvisionnements') ??
        this.missingDestinationCount() > 0
      );
    }
    if (item.route === '/expeditions') {
      return (
        this.notificationDotTest.override('expeditions') ??
        this.missingOriginCount() > 0
      );
    }
    return false;
  }

  /**
   * The i18n key for a nav item's notification-dot tooltip — Expéditions need a
   * stock origin, Approvisionnements need an on-site destination.
   */
  notificationDotTooltipKey(item: NavItem): string {
    return item.route === '/expeditions'
      ? 'missing_origin_notification_tooltip'
      : 'missing_destination_notification_tooltip';
  }

  //used to initialize mother db labels in the sidebar
  //as these are not in the same scope as 'ui/sidebar', we are initializing and setting them up separately
  //particularly to trigger on language switch while the app is running
  initMotherDbLabels() {
    //load scope and translate mother db ref groups
    void this.i18n.loadScope('features/motherdb');

    //subscribe to and translate motherdb refs whenever language changes
    this.i18n.scopeLoaded$.subscribe((loadedScope) => {
      if (loadedScope === 'features/motherdb') {
        this.translateMotherDbReferenceGroups();
      }
    });

    //set up for scope ready and fetching mother db refs
    effect(() => {
      if (
        this.motherdbCollections().length > 0 &&
        this.i18n.getScopeReady('features/motherdb')()
      ) {
        this.translateMotherDbReferenceGroups();
      }
    });
  }

  toggleRefGroup(group: string): void {
    this.expandedRefGroups.update((current) => {
      const next = new Set(current);
      if (next.has(group)) {
        next.delete(group);
      } else {
        next.add(group);
      }
      return next;
    });
  }

  isRefGroupExpanded(group: string): boolean {
    return this.expandedRefGroups().has(group);
  }
  protected readonly Permissions = Permissions;
  protected readonly SECTIONS = SECTIONS;

  /** The Tableaux caption shows when any of its data-table sections is visible. */
  protected readonly tableauxVisible = computed(
    () =>
      (this.permissionService?.canViewSection(SECTIONS.CUSTOM_VIEWS) ??
        false) ||
      (this.permissionService?.canViewSection(SECTIONS.TABLE_DATA) ?? false) ||
      (this.permissionService?.canViewSection(SECTIONS.TIMESERIES) ?? false),
  );

  /**
   * Exploitation - Pilotage: coming-soon placeholders, plus the built Suivi
   * biologie / Production / Objectives. Each carries its `section`, so it is
   * only shown — greyed/disabled — to callers who have `view` access to that
   * section (see `visiblePilotageNavItems`); callers without access don't see it
   * at all. When a feature lands, drop its `disabled` flag and the same section
   * gate then enables it.
   *
   * Order (ADEV-1134): Synoptique and Suivi Ration first
   * (`pilotageHeadNavItems`), then the Pilotage Ration group
   * (`rationProductionGroup`), then the items below (`pilotageNavItems`) with
   * Objectifs last; the greyed placeholders keep their spots.
   */
  protected readonly pilotageHeadNavItems: NavItem[] = [
    {
      label: 'nav_synoptique',
      icon: 'schema',
      route: '/synoptique',
      section: SECTIONS.SYNOPTIQUE,
      disabled: false,
    },
    {
      label: 'nav_suivi_ration',
      icon: 'donut_small',
      route: '/suivi-ration',
      section: SECTIONS.SUIVI_RATION,
      disabled: false,
    },
  ];

  protected readonly pilotageNavItems: NavItem[] = [
    {
      label: 'nav_suivi_biologie',
      icon: 'biotech',
      route: '/suivi-biologie',
      section: SECTIONS.SUIVI_BIOLOGIE,
    },
    {
      label: 'nav_suivi_transferts',
      icon: 'sync_alt',
      route: '/suivi-transferts',
      section: SECTIONS.SUIVI_TRANSFERTS,
      disabled: false,
    },
    {
      label: 'nav_production',
      icon: 'factory',
      route: '/production',
      section: SECTIONS.PRODUCTION,
      disabled: false,
    },
    {
      label: 'nav_energies_utilites',
      icon: 'bolt',
      route: '/energies-utilites',
      section: SECTIONS.ENERGIES_UTILITES,
      disabled: false,
    },
    {
      label: 'nav_suivi_site',
      icon: 'monitoring',
      route: '/suivi-site',
      section: SECTIONS.SUIVI_SITE,
      disabled: false,
    },
    // Out of the Pilotage Ration group, as the last Pilotage item (ADEV-1134).
    {
      label: 'nav_objectives',
      icon: 'flag',
      route: '/objectives',
      section: SECTIONS.OBJECTIFS,
    },
  ];

  protected readonly rationProductionGroup: NavGroup = {
    label: 'nav_cible_ration_production',
    icon: 'track_changes',
    children: [
      {
        label: 'nav_ration_objectives',
        route: '/ration/objectives',
        section: SECTIONS.RATION_TYPE,
      },
      {
        label: 'nav_ration_planning',
        route: '/ration-weekly-planning',
        section: SECTIONS.RATION_PLANNING,
      },
      {
        label: 'nav_ration_loading_requirement',
        route: '/ration/loading-requirement',
        section: SECTIONS.RATION_PLANNING,
      },
    ],
  };

  protected readonly rationProductionExpanded = signal(false);

  protected readonly rationProductionVisible = computed(() => {
    // Touches `lastResolvedPlantId` so this re-evaluates once the access
    // cache loads — same trick `isNavItemVisible`/`isChildVisible` use;
    // `canViewSection` alone isn't a tracked signal read. Evaluated as
    // separate statements, not a short-circuiting `||` chain, so both
    // children's gates are always checked (mirrors `operationsSiteVisible`).
    this.permissionService?.lastResolvedPlantId();
    const canViewObjectives =
      this.permissionService?.canViewSection(SECTIONS.RATION_TYPE) ?? false;
    const canViewPlanning =
      this.permissionService?.canViewSection(SECTIONS.RATION_PLANNING) ?? false;
    return canViewObjectives || canViewPlanning;
  });

  /** One of the group's pages is open (the header highlights, e.g. collapsed). */
  protected readonly rationProductionActive = computed(() => {
    const url = this.currentUrl();
    return this.rationProductionGroup.children.some((child) =>
      url.startsWith(child.route),
    );
  });

  /**
   * Expanded sidebar: toggles the group. Collapsed (no children shown): opens
   * the first page the caller may see, so its pages stay reachable.
   */
  protected onClickRationProduction(): void {
    if (!this.collapsed) {
      this.rationProductionExpanded.set(!this.rationProductionExpanded());
      return;
    }
    const first = this.rationProductionGroup.children.find((child) =>
      this.isChildVisible(child),
    );
    if (first) this.navigateToChild(first);
  }

  /** Pilotage shows for a single-plant project (mirrors the other operational groups). */
  protected readonly pilotageVisible = computed(
    () => !this.isMultiPlantProject(),
  );

  /**
   * Pilotage items the caller may actually see: only those they have `view`
   * access to their section (coming-soon placeholders included, still disabled).
   * Reactive: recomputes when access resolves. The caption + loop hide entirely
   * when this is empty.
   */
  protected readonly visiblePilotageNavItems = computed(() =>
    this.pilotageNavItems.filter((item) => this.isNavItemVisible(item)),
  );

  /** Same as `visiblePilotageNavItems`, for the items above the Pilotage Ration group. */
  protected readonly visiblePilotageHeadNavItems = computed(() =>
    this.pilotageHeadNavItems.filter((item) => this.isNavItemVisible(item)),
  );

  /** Routes of the Pilotage dropdowns currently open. */
  protected readonly expandedPilotageGroups = signal<ReadonlySet<string>>(
    new Set(),
  );

  protected isPilotageGroupExpanded(item: NavItem): boolean {
    return this.expandedPilotageGroups().has(item.route);
  }

  protected togglePilotageGroup(item: NavItem): void {
    this.expandedPilotageGroups.update((open) => {
      const next = new Set(open);
      if (next.has(item.route)) {
        next.delete(item.route);
      } else {
        next.add(item.route);
      }
      return next;
    });
  }

  /** Exploitation - Gestion des flux caption shows when any of its groups is visible. */
  protected readonly exploitationVisible = computed(
    () =>
      this.planningVisible() ||
      this.approvisionnementsVisible() ||
      this.expeditionsVisible() ||
      this.correctionsVisible() ||
      this.incorporationsVisible() ||
      this.biodecVisible() ||
      this.stocksVisible() ||
      this.digestatVisible(),
  );

  /**
   * Biodéconditionnement entry: its own item after Incorporations, with its own
   * section, since sidebar V2 (ADEV-1046); was a child of the Incorporations group.
   */
  protected readonly biodecNavItem: NavItem = {
    label: 'nav_biodec',
    icon: 'recycling',
    route: '/biodec',
    section: SECTIONS.BIODEC,
  };

  protected readonly biodecVisible = computed(
    () =>
      (this.permissionService?.canViewSection(SECTIONS.BIODEC) ?? false) &&
      !this.hideForMultiPlant(this.biodecNavItem.route),
  );

  /**
   * Operator checklist (ADEV-1041): shown to callers with `view` on
   * CHECKLIST_OPERATEUR, hidden from everyone else and for multi-plant projects.
   */
  protected readonly checklistOperateurNavItem: NavItem = {
    label: 'nav_checklist_operateur',
    icon: 'checklist',
    route: '/checklist-operateur',
    section: SECTIONS.CHECKLIST_OPERATEUR,
  };

  protected readonly checklistOperateurVisible = computed(
    () =>
      (this.permissionService?.canViewSection(SECTIONS.CHECKLIST_OPERATEUR) ??
        false) &&
      !this.hideForMultiPlant(this.checklistOperateurNavItem.route),
  );

  /**
   * Digestat operator input (Saisie). A single Operations item per the client
   * sidebar mock; the projected-stock dashboard lives separately under the
   * Tableaux de bord › Operations - Site group. Section-only gated (DIGESTAT).
   */
  protected readonly digestatNavItem: NavItem = {
    label: 'nav_digestat',
    icon: 'compost',
    route: '/digestat',
  };

  protected readonly digestatVisible = computed(
    () =>
      (this.permissionService?.canViewSection(SECTIONS.DIGESTAT) ?? false) &&
      !this.hideForMultiPlant(this.digestatNavItem.route),
  );
  protected readonly redNavItem: NavItem = {
    label: 'nav_red',
    icon: 'summarize',
    route: '/red',
  };
  protected readonly redVisible = computed(
    () =>
      (this.permissionService?.canViewSection(SECTIONS.RED) ?? false) &&
      !this.hideForMultiPlant(this.redNavItem.route),
  );
  protected readonly alarmesNavItem: NavItem = {
    label: 'nav_alarmes',
    icon: 'notifications_active',
    route: '/alarmes',
    section: SECTIONS.ALARMS,
    disabled: true,
  };
  protected readonly showDeactivatedSidebarItems = false;

  private readonly deactivatedPlaceholderNavItems: NavItem[] = [
    {
      label: 'notifications',
      icon: 'notifications',
      route: '/notifications',
      disabled: true,
    },
    { label: 'trends', icon: 'trending_up', route: '/trends', disabled: true },
    {
      label: 'assets',
      icon: 'precision_manufacturing',
      route: '/assets',
      disabled: true,
    },
    {
      label: 'analytics',
      icon: 'analytics',
      route: '/analytics',
      disabled: true,
    },
    {
      label: 'simulation',
      icon: 'model_training',
      route: '/simulation',
      disabled: true,
    },
    {
      label: 'optimization',
      icon: 'tune',
      route: '/optimization',
      disabled: true,
    },
  ];

  // labels are translation keys, to be used with the i18n pipe in the template
  // Dashboards are rendered as a dedicated block (see the template + the
  // dashboards-block state below), not through this generic nav loop, because
  // they need OR-permission gating, per-row hide, a Hidden group and an admin
  // "All dashboards" browse. Overviews are merged into that single list.
  protected readonly navItems: NavItem[] = [
    {
      label: 'insights',
      symbolIcon: 'psychology',
      route: '/insights',
      requiresProject: true,
      viewPermission: Permissions.VIEW_METRICS,
    },
    {
      label: 'nav_planning',
      icon: 'event_note',
      route: '/planning',
      requiresProject: true,
    },
    {
      label: 'approvisionnements',
      icon: 'local_shipping',
      route: '/approvisionnements',
      requiresProject: true,
    },
    {
      label: 'nav_expeditions',
      icon: 'outbound',
      route: '/expeditions',
      requiresProject: true,
    },
    {
      label: 'nav_corrections',
      icon: 'edit_note',
      route: '/corrections',
      requiresProject: true,
    },
    {
      label: 'reports',
      icon: 'description',
      route: '/reports',
      requiresProject: true,
      viewPermission: Permissions.VIEW_REPORTS,
      editPermission: Permissions.EDIT_REPORTS,
    },
    {
      label: 'flare_analysis',
      icon: 'local_fire_department',
      route: '/flare-analysis',
      requiresProject: true,
      viewPermission: Permissions.VIEW_FLARE_ANALYSIS,
    },
    {
      label: 'custom_views',
      icon: 'table_rows',
      route: '/custom-views',
      requiresProject: true,
      viewPermission: Permissions.VIEW_CUSTOM_VIEWS,
      editPermission: Permissions.EDIT_CUSTOM_VIEWS,
    },
    ...(this.showDeactivatedSidebarItems
      ? this.deactivatedPlaceholderNavItems
      : []),
  ];

  protected readonly isMultiPlantProject = computed(
    () => this.projectService.currentProject()?.isMultiPlant ?? false,
  );

  /** Whether `route` should be hidden given the current project's plant count. */
  protected hideForMultiPlant(route: string): boolean {
    return this.isMultiPlantProject() && isHiddenForMultiPlant(route);
  }

  @Input() collapsed = false;
  @Output() readonly timeseriesPopoutToggle = new EventEmitter<void>();
  @Output() readonly managedDataPopoutToggle = new EventEmitter<void>();
  @Output() readonly customViewsPopoutToggle = new EventEmitter<void>();

  protected activeRoute = signal<string>('/overview');
  protected currentUrl = signal<string>('/');
  protected activeSubroute = '';

  readonly weighbridgeExpanded = signal(false);
  readonly parametrageExpanded = signal(false);
  readonly incorporationsExpanded = signal(false);
  readonly stocksExpanded = signal(false);
  // "Données Site" (Gisements + reference parties) expand state.
  readonly intrantsDbExpanded = signal(false);
  readonly dataManagementRouteActive = signal(false);
  readonly weighbridgeRouteActive = signal(false);
  readonly timeseriesRouteActive = signal(false);
  readonly customViewsRouteActive = signal(false);

  // MotherDB group header (children are rendered as the Views / Reference Data
  // sections + the Sync Status link below — not via NavGroup.children).
  protected readonly motherdbSyncStatus: NavChild = {
    label: 'nav_motherdb_sync_status',
    route: '/motherdb/sync-status',
    permission: Permissions.VIEW_MOTHERDB_SYNC_STATUS,
  };

  // Collections fetched once the user can view Données entrantes (gated in the
  // constructor so others never trigger /api/motherdb calls).
  private readonly motherdbCollections = signal<MotherDbCollectionDto[]>([]);
  private motherdbFetched = false;
  readonly expandedRefGroups = signal<ReadonlySet<string>>(new Set());
  /** « Fichiers pont bascule » is collapsible and collapsed by default, like the groups of Données entrantes. */
  readonly weighbridgeFilesExpanded = signal(false);

  readonly canEditReports = computed(() =>
    this.permissionService?.canEditSection(SECTIONS.REPORTING),
  );
  readonly canViewReports = computed(() =>
    this.permissionService?.canViewSection(SECTIONS.REPORTING),
  );
  /**
   * i18n key for a MotherDB collection's nav label. Translations live in the
   * `ui/sidebar` scope; the `| i18n` pipe (impure) re-resolves on language switch.
   */
  private motherdbLabelKey(id: string): string {
    return `nav_motherdb_col_${id}`;
  }

  /** View collections (vi_*) as nav children (labels are i18n keys), sorted. */
  private readonly motherdbViewLinks = computed(() =>
    this.motherdbCollections()
      .filter((c) => c.category === 'view')
      .sort((a, b) => a.label.localeCompare(b.label))
      .map((c) => ({
        id: c.id,
        label: this.motherdbLabelKey(c.id),
        route: `/motherdb/collections/${c.id}`,
      })),
  );

  /**
   * View collections listed flat. Catalogue intrants (`feedstock`) is left out:
   * it is « BDD Intrants » below (ADEV-1207); the energy views have their folder.
   */
  readonly motherdbViews = computed<NavChild[]>(() =>
    this.motherdbViewLinks()
      .filter((c) => c.id !== BDD_INTRANTS_VIEW && !ENERGIE_VIEWS.includes(c.id))
      .map(({ label, route }) => ({ label, route })),
  );

  /** The « Energie » folder of Données entrantes (CVES-12, ADEV-1207). */
  readonly motherdbEnergieViews = computed<NavChild[]>(() =>
    this.motherdbViewLinks()
      .filter((c) => ENERGIE_VIEWS.includes(c.id))
      .map(({ label, route }) => ({ label, route })),
  );
  readonly energieExpanded = signal(false);

  /** Source collections grouped by source system for the Reference Data section. */
  readonly motherdbReferenceGroups = signal<
    {
      group: string;
      items: NavChild[];
    }[]
  >([]);

  /**
   * « Fichiers pont bascule » under Données sortantes (ADEV-1207; « Sources des
   * fichiers CSV » under Données entrantes before): the five generated weighbridge
   * reference files, each opening the combined content actually dropped to the
   * scale (Biolid + Regener + Ecovalim + Autre), not a single raw source
   * collection. Labelled by filename, so static: it needs no MotherDB fetch.
   */
  readonly weighbridgeFiles: readonly NavChild[] = WEIGHBRIDGE_FILES.map((f) => ({
    label: f.filename,
    route: f.route,
  }));

  readonly motherdbReferenceGroupsRaw = computed(() => {
    // All raw source collections stay browsable under their per-system groups. The
    // « Fichiers pont bascule » links point at the generated files (combined
    // content), so the raw upstream collections (incl. the former CSV-source ones)
    // live here.
    const sources = this.motherdbCollections()
      .filter((c) => c.category === 'source')
      .sort((a, b) => a.label.localeCompare(b.label));
    return sources;
  });

  translateMotherDbReferenceGroups() {
    const byGroup = new Map<string, NavChild[]>();

    this.motherdbReferenceGroupsRaw().forEach((source) => {
      const key = source.group ?? 'Other';

      const items = byGroup.get(key) ?? [];

      items.push({
        label: this.i18n.t(source.id, 'features/motherdb'),
        route: `/motherdb/collections/${source.id}`,
      });

      byGroup.set(key, items);
    });

    const translated = [...byGroup.entries()]
      .map(([group, items]) => ({ group, items }))
      .sort((a, b) => a.group.localeCompare(b.group));

    this.motherdbReferenceGroups.set(translated);
  }

  // Mock: Approvisionnements and Expeditions are separate top-level items, not a
  // grouped "procurement" expandable.
  protected readonly planningNavItem = this.navItems.find(
    (i) => i.route === '/planning',
  );
  protected readonly approvisionnementsNavItem = this.navItems.find(
    (i) => i.route === '/approvisionnements',
  );
  protected readonly expeditionsNavItem = this.navItems.find(
    (i) => i.route === '/expeditions',
  );
  protected readonly correctionsNavItem = this.navItems.find(
    (i) => i.route === '/corrections',
  );

  protected readonly planningVisible = computed(
    () =>
      (this.permissionService?.canViewSection(SECTIONS.PLANNING) ?? false) &&
      !this.hideForMultiPlant('/planning'),
  );
  protected readonly approvisionnementsVisible = computed(
    () =>
      (this.permissionService?.canViewSection(SECTIONS.APPROVISIONNEMENTS) ??
        false) &&
      !this.hideForMultiPlant('/approvisionnements'),
  );
  protected readonly expeditionsVisible = computed(
    () =>
      (this.permissionService?.canViewSection(SECTIONS.EXPEDITIONS) ?? false) &&
      !this.hideForMultiPlant('/expeditions'),
  );
  /**
   * Corrections console (ADEV-1075): Corrections at edit (ADEV-1207,
   * CORRECTIONS_ACCESS) and at least view on a flow it corrects (Approvisionnements
   * or Expéditions); the page offers only those flows, so without one it would be
   * empty.
   */
  protected readonly correctionsVisible = computed(
    () =>
      (this.permissionService?.canEditSection(SECTIONS.CORRECTIONS) ?? false) &&
      ((this.permissionService?.canViewSection(SECTIONS.APPROVISIONNEMENTS) ??
        false) ||
        (this.permissionService?.canViewSection(SECTIONS.EXPEDITIONS) ??
          false)) &&
      !this.hideForMultiPlant('/corrections'),
  );

  /**
   * Matières group. Gisements is the first subcategory; Producteurs /
   * Transporteurs / Analyses can follow.
   */
  protected readonly matieresGroup: NavGroup = {
    label: 'nav_matieres',
    icon: 'category',
    children: [
      {
        label: 'nav_gisements',
        route: '/matieres/gisements',
        section: SECTIONS.GISEMENTS,
      },
    ],
  };

  protected readonly matieresVisible = computed(
    () =>
      (this.permissionService?.canViewSection(SECTIONS.GISEMENTS) ?? false) &&
      !this.hideForMultiPlant(this.matieresGroup.children[0].route),
  );

  /**
   * "Données Site" group (sidebar V2, ADEV-1046; was "Base de données
   * Intrants"): Gisements and the producer / supplier / transporter reference
   * pages. Producers/suppliers/transporters are MotherDB registry collections,
   * so these deep-link into the collection browser. The MotherDB browser itself
   * is the "Données entrantes" group below.
   */
  protected readonly intrantsDbGroup: Pick<NavGroup, 'label' | 'icon'> = {
    label: 'nav_donnees_site',
    icon: 'storage',
  };

  private readonly intrantsDbDirectLinksRaw: NavChild[] = [
    // Gisements is the editable feedstock configuration (on-site destination /
    // origin per feedstock); the feedstock catalogue is the read-only MotherDB
    // reference, so it follows the configuration page.
    {
      label: 'nav_gisements',
      route: '/matieres/gisements',
      section: SECTIONS.GISEMENTS,
    },
    // Unified party pages: Wintrans + Regener + Ecovalim merged (the raw per-source
    // collections stay browsable under the Wintrans/Ecovalim groups below).
    {
      label: 'nav_producteurs',
      route: '/motherdb/reference-parties/producer',
      section: REFERENCE_PARTY_SECTIONS.producer,
    },
    {
      label: 'nav_fournisseurs',
      route: '/motherdb/reference-parties/supplier',
      section: REFERENCE_PARTY_SECTIONS.supplier,
    },
    {
      label: 'nav_transporteurs',
      route: '/motherdb/reference-parties/transporter',
      section: REFERENCE_PARTY_SECTIONS.transporter,
    },
  ];

  /** Direct links minus any hidden for the current multi-plant project. */
  readonly intrantsDbDirectLinks = computed<NavChild[]>(() =>
    this.intrantsDbDirectLinksRaw.filter(
      (child) => !this.hideForMultiPlant(child.route),
    ),
  );

  protected readonly intrantsDbVisible = computed(
    () => this.matieresVisible() || this.motherdbVisible(),
  );

  /**
   * "Données entrantes" group (sidebar V2, ADEV-1046): what arrives from the
   * other systems. The MotherDB views (vi_*, the energy ones in their Energie
   * folder), the per-system source collections (Wintrans, Ecovalim, …), the
   * feedstock catalogue ("BDD Intrants", moved here from Données Site) and the
   * sync status. The weighbridge files moved to Données sortantes (ADEV-1207).
   */
  protected readonly donneesEntrantesGroup: Pick<NavGroup, 'label' | 'icon'> = {
    label: 'nav_motherdb_views',
    icon: 'move_to_inbox',
  };

  protected readonly bddIntrantsLink: NavChild = {
    label: 'nav_bdd_intrants',
    route: `/motherdb/collections/${BDD_INTRANTS_VIEW}`,
    section: SECTIONS.DONNEES_ENTRANTES,
  };

  readonly donneesEntrantesExpanded = signal(false);

  onClickDonneesEntrantes() {
    if (!this.collapsed) {
      this.donneesEntrantesExpanded.set(!this.donneesEntrantesExpanded());
      return;
    }
    this.navigateToChild(this.bddIntrantsLink);
  }

  /**
   * "Données sortantes": what Anessa sends out. Today « Fichiers pont bascule »,
   * the files dropped to the weighbridge (ADEV-1207); the files sent to Wintrans /
   * Ecovalim are to come.
   */
  protected readonly donneesSortantesGroup: Pick<NavGroup, 'label' | 'icon'> = {
    label: 'nav_motherdb_outgoing_data',
    icon: 'outbox',
  };

  readonly donneesSortantesExpanded = signal(false);

  onClickDonneesSortantes() {
    if (!this.collapsed) {
      this.donneesSortantesExpanded.set(!this.donneesSortantesExpanded());
      return;
    }
    this.navigateToChild(this.weighbridgeFiles[0]);
  }

  onClickIntrantsDb() {
    if (!this.collapsed) {
      this.intrantsDbExpanded.set(!this.intrantsDbExpanded());
      return;
    }
    const first = this.intrantsDbDirectLinks()[0];
    if (first) this.navigateToChild(first);
  }

  /**
   * Paramétrage (configuration) group. Destinations is the first subcategory;
   * more config pages (Gisements source lists, etc.) will be added here.
   */
  // "Caractéristiques du site" group.
  protected readonly parametrageGroup: NavGroup = {
    label: 'nav_caracteristiques_site',
    icon: 'settings',
    children: [
      {
        label: 'nav_informations_site',
        route: '/parametrage/informations-site',
        section: SECTIONS.INFORMATIONS_SITE,
      },
      {
        label: 'nav_conception',
        route: '/parametrage/destinations',
        section: SECTIONS.CONCEPTION,
      },
      {
        label: 'nav_contenant',
        route: '/parametrage/contenants',
        section: SECTIONS.CONTENANTS,
      },
      {
        label: 'nav_assumptions',
        route: '/parametrage/assumptions',
        section: SECTIONS.ASSUMPTIONS,
      },
    ],
  };

  onClickConfiguration() {
    if (!this.collapsed)
      this.parametrageExpanded.set(!this.parametrageExpanded());
    else this.navigateTo(this.parametrageGroup.children[0]);
  }

  /** The Paramétrage group shows when the user can see any of its subcategories. */
  protected readonly parametrageVisible = computed(
    () =>
      ((this.permissionService?.canViewSection(SECTIONS.CONCEPTION) ?? false) ||
        (this.permissionService?.canViewSection(SECTIONS.CONTENANTS) ??
          false) ||
        (this.permissionService?.canViewSection(SECTIONS.ASSUMPTIONS) ??
          false) ||
        (this.permissionService?.canViewSection(SECTIONS.INFORMATIONS_SITE) ??
          false)) &&
      !this.hideForMultiPlant('/parametrage/destinations'),
  );

  /**
   * Incorporations group. Saisie (liquides/solides behind one phase toggle) /
   * dilutions (entry) + the read-only Synthèse journalière and Synthèse
   * hebdomadaire (BIODEC excluded from the weekly rollup, since it's not a
   * real digester-fed incorporation). Biodéconditionnement is its own item
   * since sidebar V2 (ADEV-1046).
   */
  protected readonly incorporationsGroup: NavGroup = {
    label: 'nav_incorporations',
    icon: 'science',
    children: [
      {
        label: 'nav_saisie',
        route: '/incorporations/saisie',
        section: SECTIONS.INCORPORATIONS,
      },
      {
        label: 'nav_dilutions',
        route: '/incorporations/dilutions',
        section: SECTIONS.INCORPORATIONS,
      },
      {
        label: 'nav_saisie_detail',
        route: '/incorporations/detail',
        section: SECTIONS.INCORPORATIONS,
      },
      {
        label: 'nav_suivi_hebdo',
        route: '/incorporations/suivi-hebdomadaire',
        section: SECTIONS.INCORPORATIONS,
      },
    ],
  };

  protected readonly incorporationsVisible = computed(
    () =>
      (this.permissionService?.canViewSection(SECTIONS.INCORPORATIONS) ??
        false) &&
      !this.hideForMultiPlant(this.incorporationsGroup.children[0].route),
  );

  /**
   * Stocks group. Composition (per filière / intrant / gisement, with the manual
   * adjustment flow) + Statut (level vs Destination capacity). Both are as-of-date
   * views over the reception / incorporation / adjustment ledger.
   */
  protected readonly stocksGroup: NavGroup = {
    label: 'nav_stocks',
    icon: 'inventory_2',
    children: [
      {
        label: 'nav_stocks_composition',
        route: '/stocks/composition',
        section: SECTIONS.STOCKS,
      },
      {
        label: 'nav_stocks_statut',
        route: '/stocks/statut',
        section: SECTIONS.STOCKS,
      },
    ],
  };

  protected readonly stocksVisible = computed(
    () =>
      (this.permissionService?.canViewSection(SECTIONS.STOCKS) ?? false) &&
      !this.hideForMultiPlant(this.stocksGroup.children[0].route),
  );

  /**
   * The standalone Weighbridge data-management section is hidden for now: import
   * and viewing moved to Approvisionnements › Réception / Expédition. Flip to
   * true to restore the section (routes are still registered).
   */
  protected readonly showWeighbridgeSection = false;

  protected readonly weighbridgeGroup: NavGroup = {
    label: 'weighbridge',
    icon: 'scale',
    children: [
      {
        label: 'nav_weighbridge_data',
        route: '/data-management/weighbridge/data',
      },
      {
        label: 'nav_weighbridge_mapping',
        route: '/data-management/weighbridge/mapping',
      },
    ],
  };

  /**
   * Tableaux de bord – Opérations: the static, non-configurable boards, listed
   * under their own caption above the personal dashboards (sidebar V2,
   * ADEV-1046), in the client's order. Each has its own view-only section; the
   * boards not built yet stay greyed (and hidden from roles without access).
   */
  protected readonly operationsBoardsNavItems: NavItem[] = [
    {
      label: 'nav_synthese_site',
      icon: 'today',
      route: `${this.OPERATIONS_SITE_PREFIX}/synthese-site`,
      section: SECTIONS.DASH_PRODUCTION_JOURNALIERE,
      disabled: true,
    },
    {
      label: 'nav_incorporations_dashboard',
      icon: 'science',
      route: `${this.OPERATIONS_SITE_PREFIX}/incorporations`,
      section: SECTIONS.DASH_INCORPORATIONS,
    },
    {
      label: 'nav_biodec_dashboard',
      icon: 'recycling',
      route: `${this.OPERATIONS_SITE_PREFIX}/biodec`,
      section: SECTIONS.DASH_BIODEC,
    },
    {
      label: 'nav_stocks_matieres_niveaux',
      icon: 'inventory_2',
      route: `${this.OPERATIONS_SITE_PREFIX}/stocks-niveaux`,
      section: SECTIONS.DASH_STOCKS_NIVEAUX,
    },
    {
      label: 'nav_digestat',
      icon: 'compost',
      route: `${this.OPERATIONS_SITE_PREFIX}/digestat`,
      section: SECTIONS.DASH_DIGESTAT,
    },
    {
      label: 'nav_biologie_qualite',
      icon: 'biotech',
      route: `${this.OPERATIONS_SITE_PREFIX}/biologie-qualite`,
      section: SECTIONS.DASH_BIOLOGIE_QUALITE,
    },
    {
      label: 'nav_production_dashboard',
      icon: 'factory',
      route: `${this.OPERATIONS_SITE_PREFIX}/production`,
      section: SECTIONS.DASH_PRODUCTION,
    },
    {
      label: 'nav_energy_dashboard',
      icon: 'bolt',
      route: `${this.OPERATIONS_SITE_PREFIX}/energie`,
      section: SECTIONS.DASH_PERFORMANCE_ENERGETIQUE,
    },
    {
      label: 'nav_production_hebdomadaire',
      icon: 'date_range',
      route: `${this.OPERATIONS_SITE_PREFIX}/production-hebdomadaire`,
      section: SECTIONS.DASH_PRODUCTION_HEBDOMADAIRE,
      disabled: true,
    },
    {
      label: 'nav_production_mensuelle',
      icon: 'calendar_month',
      route: `${this.OPERATIONS_SITE_PREFIX}/production-mensuelle`,
      section: SECTIONS.DASH_PRODUCTION_MENSUELLE,
      disabled: true,
    },
    {
      label: 'nav_cockpit_production',
      icon: 'speed',
      route: `${this.OPERATIONS_SITE_PREFIX}/cockpit-production`,
      section: SECTIONS.DASH_COCKPIT_PRODUCTION,
      disabled: true,
    },
    {
      label: 'nav_flux_matieres',
      icon: 'swap_horiz',
      route: `${this.OPERATIONS_SITE_PREFIX}/flux-matieres`,
      section: SECTIONS.DASH_FLUX_MATIERES,
      disabled: true,
    },
  ];

  /** The boards the caller may see (built or greyed), none for a multi-plant project. */
  protected readonly visibleOperationsBoards = computed(() =>
    this.operationsBoardsNavItems.filter(
      (item) =>
        this.isNavItemVisible(item) && !this.hideForMultiPlant(item.route),
    ),
  );

  /** The "Tableaux de bord – Opérations" caption shows when any board is visible. */
  protected readonly operationsSiteVisible = computed(
    () => this.visibleOperationsBoards().length > 0,
  );

  /**
   * A board's URL is under /dashboard, so it is matched on its own path, not on
   * the first segment; a whole path segment, so /production doesn't light up on
   * /production-hebdomadaire.
   */
  protected isOperationsBoardActive(item: NavItem): boolean {
    const path = this.currentUrl().split(/[?#]/)[0];
    return path === item.route || path.startsWith(`${item.route}/`);
  }

  /**
   * Whether a group child is visible for the current role. Section-gated
   * children (e.g. Digestat) check leveled access; the rest keep their
   * capability gate. Reads `lastResolvedPlantId` so the `@if` re-evaluates
   * once the permission cache loads (mirrors the appHasPermission directive).
   */
  protected isChildVisible(child: NavChild): boolean {
    this.permissionService?.lastResolvedPlantId();
    if (child.section) {
      return this.permissionService?.canViewSection(child.section) ?? false;
    }
    if (child.permission) {
      return this.permissionService?.hasPermission(child.permission) ?? false;
    }
    return true;
  }

  onClickWeighBridge() {
    if (!this.collapsed)
      this.weighbridgeExpanded.set(!this.weighbridgeExpanded());
    else this.navigateTo(this.weighbridgeGroup.children[0]);
  }
  protected readonly timeseriesDataGroup = computed<NavGroup>(() => ({
    label: 'timeseries_data',
    icon: 'table_chart',
    children: this.timeseriesTablesService
      .tables()
      .filter((t) => t.visible)
      .sort((a, b) => a.order - b.order)
      .map((table) => ({
        label: table.name,
        route: `/timeseries-data/${table.id}`,
        isRawLabel: true,
      })),
  }));

  ngOnInit() {
    this.syncActiveStateFromUrl(this.router.url);
    void this.permissionService?.refreshPermissionsForActivePlant({
      force: true,
    });
  }

  /** Canonical route for the single merged dashboards section. */
  readonly DASHBOARD_ROUTE = '/dashboard';

  /** Dashboards nav shows for users who can view individual OR shared dashboards. */
  readonly canViewDashboards = computed(
    () => this.permissionService?.canViewSection(SECTIONS.DASHBOARDS) ?? false,
  );

  readonly canEditDashboards = computed(
    () => this.permissionService?.canEditSection(SECTIONS.DASHBOARDS) ?? false,
  );

  /** Admins can browse every dashboard they can access, not just owned/shared. */
  readonly canBrowseAllDashboards = computed(
    () =>
      this.permissionService?.hasPermission(Permissions.GLOBAL_ADMIN) ?? false,
  );

  /** Admins can browse every report they can access, not just owned/shared. */
  readonly canBrowseAllReports = computed(
    () =>
      this.permissionService?.hasPermission(Permissions.GLOBAL_ADMIN) ?? false,
  );

  readonly dashboardSectionActive = computed(() => {
    const url = this.currentUrl();

    if (url.startsWith(this.OPERATIONS_SITE_PREFIX)) {
      return false;
    }

    return (
      this.DASHBOARD_ROUTES.some((route) => url.startsWith(route)) &&
      !!this.activeSubroute
    );
  });

  readonly reportSectionActive = computed(
    () => this.activeRoute() === this.REPORT_ROUTE,
  );

  // Flat items rendered explicitly (not via a loop) so they slot under the client's
  // section captions. Data source stays `navItems` (single source of truth).
  protected readonly insightsNavItem = this.navItems.find(
    (i) => i.route === '/insights',
  );
  protected readonly reportsNavItem = this.navItems.find(
    (i) => i.route === '/reports',
  );
  protected readonly flareNavItem = this.navItems.find(
    (i) => i.route === '/flare-analysis',
  );

  readonly canViewFlare = computed(
    () =>
      (this.permissionService?.canViewSection(SECTIONS.TORCHAGE) ?? false) &&
      !this.hideForMultiPlant('/flare-analysis'),
  );
  readonly canViewInsights = computed(
    () =>
      (this.permissionService?.canViewSection(SECTIONS.MEASURES) ?? false) &&
      !this.hideForMultiPlant('/insights'),
  );

  /**
   * Données Site's reference party pages (Producteurs / Fournisseurs /
   * Transporteurs): any one of their sections (ADEV-1207) shows them.
   */
  protected readonly motherdbVisible = computed(
    () =>
      Object.values(REFERENCE_PARTY_SECTIONS).some(
        (section) => this.permissionService?.canViewSection(section) ?? false,
      ) && !this.hideForMultiPlant('/motherdb'),
  );

  /** "Données entrantes" group: its own section (ADEV-1192). */
  protected readonly donneesEntrantesVisible = computed(
    () =>
      (this.permissionService?.canViewSection(SECTIONS.DONNEES_ENTRANTES) ??
        false) &&
      !this.hideForMultiPlant('/motherdb'),
  );

  /** "Données sortantes": its own section (ADEV-1192). */
  protected readonly donneesSortantesVisible = computed(
    () =>
      (this.permissionService?.canViewSection(SECTIONS.DONNEES_SORTANTES) ??
        false) &&
      !this.hideForMultiPlant('/motherdb'),
  );

  /** The Configuration caption shows when any of its child sections is visible. */
  readonly configurationVisible = computed(() => {
    const p = this.permissionService;
    if (!p) return false;
    return (
      this.parametrageVisible() ||
      this.intrantsDbVisible() ||
      this.donneesEntrantesVisible() ||
      this.donneesSortantesVisible() ||
      this.canViewInsights() ||
      p.canViewSection(SECTIONS.TABLE_DATA) ||
      p.canViewSection(SECTIONS.TIMESERIES) ||
      p.canViewSection(SECTIONS.CUSTOM_VIEWS) ||
      // Alarmes is a coming-soon item in this block; a caller granted only its
      // section must still open the block to see it.
      p.canViewSection(SECTIONS.ALARMS)
    );
  });

  readonly hiddenDashboardsExpanded = signal(false);
  readonly allDashboardsExpanded = signal(false);
  readonly allDashboards = computed(() =>
    this.dashboardService.allDashboardList(),
  );

  /** Shown dashboards: owned/shared (from the backend), minus the user's hidden ones,
   * truncated to the first 3 unless the dashboards section is active. */
  readonly visibleDashboards = computed<DashboardListItem[]>(() => {
    const hidden = this.dashboardService.hiddenDashboardIds();
    const list = this.dashboardService
      .dashboardList()
      .filter((d) => !hidden.has(d.id));
    return this.dashboardSectionActive() ? list : list.slice(0, 3);
  });

  /** Dashboards the user has hidden, surfaced in a collapsed group. */
  readonly hiddenDashboards = computed<DashboardListItem[]>(() => {
    const hidden = this.dashboardService.hiddenDashboardIds();
    return this.dashboardService
      .dashboardList()
      .filter((d) => hidden.has(d.id));
  });

  readonly hiddenReportsExpanded = signal(false);
  readonly allReportsExpanded = signal(false);
  readonly allReports = computed(() => this.reportService.allReportList());

  /** Shown reports: owned/shared (from the backend), minus the user's hidden ones,
   * truncated to the first 3 unless the reports section is active. */
  readonly visibleReports = computed<ReportListItemDto[]>(() => {
    const hiddenReports = this.reportService.hiddenReportIds();
    const list = this.reportService
      .reportList()
      .filter((r) => !hiddenReports.has(r.id));
    return this.reportSectionActive() ? list : list.slice(0, 3);
  });

  /** Reports the user has hidden, surfaced in a collapsed group. */
  readonly hiddenReports = computed<ReportListItemDto[]>(() => {
    const hidden = this.reportService.hiddenReportIds();
    return this.reportService.reportList().filter((r) => hidden.has(r.id));
  });

  /** Sync active sidebar styles from a given URL path */
  syncActiveStateFromUrl(url: string) {
    this.currentUrl.set(url);
    const segments = url.split('/').filter(Boolean);
    if (segments[0]) {
      this.activeRoute.set('/' + segments[0].split('?')[0]);

      if (segments[1]) {
        this.activeSubroute = decodeURIComponent(segments[1].split('?')[0]);
        // A static board's segment is not a dashboard id — setting it here would
        // point the dashboard service at an id that does not exist.
        if (
          this.DASHBOARD_ROUTES.includes('/' + segments[0].split('?')[0]) &&
          !url.startsWith(this.OPERATIONS_SITE_PREFIX)
        ) {
          this.dashboardService.selectedDashboardID.set(this.activeSubroute);
        }
        if ('/' + segments[0] === this.REPORT_ROUTE) {
          this.reportService.selectedReportId.set(this.activeSubroute);
        }
      } else {
        this.activeSubroute = '';
      }
    } else {
      // Root path renders the project selection (plants) page
      this.activeRoute.set('/plants');
      this.activeSubroute = '';
    }

    this.syncGroupExpansion(url);
  }

  /** Split out of syncGroupExpansion for Sonar cognitive complexity; the whole file has a refactor card. */
  private syncPilotageGroupExpansion(url: string): void {
    for (const item of this.pilotageNavItems) {
      if (item.children && url.startsWith(item.route + '/')) {
        this.expandedPilotageGroups.update((open) =>
          new Set(open).add(item.route),
        );
      }
    }
  }

  /**
   * Données Site, Données entrantes (with its Energie folder) and Données
   * sortantes (with « Fichiers pont bascule ») for the given URL.
   */
  private syncDataGroupExpansion(url: string): void {
    if (url.startsWith('/motherdb/reference-parties') || url.startsWith('/matieres')) {
      this.intrantsDbExpanded.set(true);
    } else if (url.startsWith('/motherdb/reference-files')) {
      this.donneesSortantesExpanded.set(true);
      this.weighbridgeFilesExpanded.set(true);
    } else if (url.startsWith('/motherdb')) {
      this.donneesEntrantesExpanded.set(true);
      if (ENERGIE_VIEWS.some((id) => url.startsWith(`/motherdb/collections/${id}`))) {
        this.energieExpanded.set(true);
      }
    }
  }

  /** Expand/highlight the data-management style sidebar groups for the given URL. */
  private syncGroupExpansion(url: string): void {
    this.syncDataGroupExpansion(url);

    if (url.startsWith('/parametrage')) {
      this.parametrageExpanded.set(true);
    }
    if (url.startsWith('/incorporations')) {
      this.incorporationsExpanded.set(true);
    }
    if (url.startsWith('/stocks')) {
      this.stocksExpanded.set(true);
    }
    this.syncPilotageGroupExpansion(url);
    if (
      this.rationProductionGroup.children.some((child) =>
        url.startsWith(child.route),
      )
    ) {
      this.rationProductionExpanded.set(true);
    }
    if (
      !url.startsWith(this.OPERATIONS_SITE_PREFIX) &&
      this.DASHBOARD_ROUTES.some((route) => url.startsWith(route))
    ) {
      this.dashboardsExpanded.set(true);
    }
    if (url.startsWith(this.REPORT_ROUTE)) {
      this.reportsExpanded.set(true);
    }

    if (url.startsWith('/data-management/weighbridge')) {
      this.weighbridgeExpanded.set(true);
      this.weighbridgeRouteActive.set(true);
      this.dataManagementRouteActive.set(false);
      this.timeseriesRouteActive.set(false);
      this.customViewsRouteActive.set(false);
    } else if (url.startsWith('/timeseries-data')) {
      this.timeseriesRouteActive.set(true);
      this.dataManagementRouteActive.set(false);
      this.weighbridgeRouteActive.set(false);
      this.customViewsRouteActive.set(false);
    } else if (url.startsWith('/data-management')) {
      this.dataManagementRouteActive.set(true);
      this.weighbridgeRouteActive.set(false);
      this.timeseriesRouteActive.set(false);
      this.customViewsRouteActive.set(false);
    } else if (url.startsWith(this.CUSTOM_VIEWS_ROUTE)) {
      this.customViewsRouteActive.set(true);
      this.dataManagementRouteActive.set(false);
      this.weighbridgeRouteActive.set(false);
      this.timeseriesRouteActive.set(false);
    } else {
      this.dataManagementRouteActive.set(false);
      this.weighbridgeRouteActive.set(false);
      this.timeseriesRouteActive.set(false);
      this.customViewsRouteActive.set(false);
    }
  }

  navigateToChild(child: NavChild, event?: MouseEvent): void {
    if (child.disabled) return;
    const label = child.isRawLabel
      ? child.label
      : this.i18n.t(child.label, 'ui/sidebar');
    if (this.handleNewTabClick(event, child.route, label)) return;

    void this.router.navigate([child.route]);
  }

  /**
   * Ctrl/Cmd/middle-click ("open in new tab") handling shared by navigateTo,
   * navigateToChild and openDashboard. Returns true when the caller should
   * stop — a new tab was opened, or a right-click was swallowed — false when
   * it should fall through to its normal in-place navigation. With tab view
   * on, the new tab is an in-app tab; with it off, a real browser tab — the
   * nav entries aren't `<a href>` links, so the browser can't do this for
   * them on its own.
   */
  private handleNewTabClick(
    event: MouseEvent | undefined,
    route: string,
    label: string,
  ): boolean {
    if (!event) return false;
    // (auxclick) also fires for the right button: swallow it so opening the
    // context menu doesn't navigate the current tab away.
    if (event.type === 'auxclick' && event.button !== 1) return true;
    if (!isOpenInNewTabClick(event)) return false;
    if (this.tabViewService.enabled()) {
      this.tabViewService.openTab(route, label);
      return true;
    }
    const url = this.router.serializeUrl(this.router.createUrlTree([route]));
    globalThis.open(url, '_blank', 'noopener');
    return true;
  }

  isChildActive(child: NavChild): boolean {
    //decode URI as keys with spaces will show up as 'key%20space' where as child route would show 'key space'
    //look for exact string matching instead of startsWith, as startsWith would result in true if a key included another key (example, key and key2)
    //take away params in the current url before comparison
    return decodeURIComponent(this.currentUrl().split('?')[0]) === child.route;
  }

  /**
   * Whether a nav item is shown at all. A section-gated item (including a
   * coming-soon placeholder) is shown only to callers with `view` access to its
   * section; a permission-gated one only with that permission; an item with
   * neither section nor permission is always shown. Touches
   * `lastResolvedPlantId()` so it re-evaluates once the access cache loads.
   */
  protected isNavItemVisible(item: NavItem): boolean {
    this.permissionService?.lastResolvedPlantId();
    if (item.section) {
      return this.permissionService?.canViewSection(item.section) ?? false;
    }
    if (item.viewPermission) {
      return (
        this.permissionService?.hasPermission(item.viewPermission) ?? false
      );
    }
    return true;
  }

  protected isNavItemDisabled(item: NavItem): boolean {
    if (item.disabled) return true;
    if (this.lacksEveryPlantAccess(item)) return true;
    // Resolved here rather than baked into the item's `disabled` flag: the nav
    // arrays are built at construction, before the access cache loads. This runs
    // per change-detection pass and canViewSection reads signals, so the item
    // corrects itself once access resolves.
    if (item.section && !this.permissionService?.canViewSection(item.section)) {
      return true;
    }
    return !!(item.requiresProject && !this.projectService.currentProject());
  }

  /**
   * A multi-plant project's page whose section the backend requires on every
   * plant (see `MULTI_PLANT_EVERY_PLANT_SECTIONS`), held on only some of them:
   * shown greyed out with a tooltip rather than hidden, so the user knows why.
   */
  protected lacksEveryPlantAccess(item: NavItem): boolean {
    if (!this.isMultiPlantProject()) return false;
    const section = everyPlantSectionFor(item.route);
    if (!section || !this.permissionService) return false;
    return !meetsAccessLevel(
      this.permissionService.sectionLevelOnEveryPlant(section),
      ACCESS_LEVEL.VIEW,
    );
  }

  /** The tooltip explaining why a nav item is disabled, if any. */
  protected navItemTooltipKey(item: NavItem): string | null {
    if (item.requiresProject && !this.projectService.currentProject()) {
      return 'no_project_selected_tooltip';
    }
    if (this.lacksEveryPlantAccess(item)) return 'every_plant_access_required_tooltip';
    return null;
  }

  navigateTo(item: NavItem, subpath?: string, event?: MouseEvent) {
    if (this.isNavItemDisabled(item)) return;
    const route = this.resolveNavRoute(item, subpath);
    const label = this.i18n.t(item.label, 'ui/sidebar');
    if (this.handleNewTabClick(event, route.join('/'), label)) return;

    void this.router.navigate(route);
  }

  /**
   * Builds the route segments for navigateTo: an explicit subpath if given,
   * otherwise the Reports item defaults to its first visible report.
   */
  private resolveNavRoute(item: NavItem, subpath?: string): string[] {
    const route = [item.route];
    if (subpath !== null && subpath !== undefined) {
      route.push(subpath);
      return route;
    }
    if (item.route === this.REPORT_ROUTE) {
      const firstReport = this.visibleReports()[0];
      if (firstReport) route.push(firstReport.id);
    }
    return route;
  }

  /** Clicking the Dashboards header opens the first visible dashboard (or the base route). */
  openDashboardsSection() {
    if (!this.projectService.currentProject()) return;
    const first = this.visibleDashboards()[0];
    void this.router.navigate(
      first ? [this.DASHBOARD_ROUTE, first.id] : [this.DASHBOARD_ROUTE],
    );
  }

  /** Navigate to a dashboard in the merged section. */
  openDashboard(dashboardId: string, label = '', event?: MouseEvent) {
    const route = `${this.DASHBOARD_ROUTE}/${dashboardId}`;
    if (this.handleNewTabClick(event, route, label)) return;
    void this.router.navigate([this.DASHBOARD_ROUTE, dashboardId]);
  }

  hideDashboard(event: Event, dashboardId: string) {
    event.stopPropagation();
    this.dashboardService.setDashboardHidden(dashboardId, true);
  }

  unhideDashboard(event: Event, dashboardId: string) {
    event.stopPropagation();
    this.dashboardService.setDashboardHidden(dashboardId, false);
  }

  hideReport(event: Event, reportId: string) {
    event.stopPropagation();
    this.reportService.setReportHidden(reportId, true);
  }

  unhideReport(event: Event, reportId: string) {
    event.stopPropagation();
    this.reportService.setReportHidden(reportId, false);
  }

  toggleHiddenDashboardsGroup() {
    this.hiddenDashboardsExpanded.update((v) => !v);
  }

  toggleHiddenReportsGroup() {
    this.hiddenReportsExpanded.update((v) => !v);
  }

  /** Expand/collapse the admin "All dashboards" browse group, lazy-loading on first open. */
  toggleAllDashboards() {
    const next = !this.allDashboardsExpanded();
    this.allDashboardsExpanded.set(next);
    if (next && !this.dashboardService.allDashboardsListLoaded()) {
      void firstValueFrom(this.dashboardService.fetchAllAccessibleDashboards());
    }
  }

  /** Expand/collapse the admin "All reports" browse group, lazy-loading on first open. */
  async toggleAllReports() {
    const next = !this.allReportsExpanded();
    this.allReportsExpanded.set(next);
    if (next && !this.reportService.allReportListLoaded()) {
      await this.reportService.fetchAllAccessibleReports();
    }
  }

  onDashboardDrop(event: CdkDragDrop<DashboardListItem[]>) {
    if (event.previousIndex === event.currentIndex) return;

    const hidden = this.dashboardService.hiddenDashboardIds();
    const all = this.dashboardService.dashboardList();
    const visible = all.filter((d) => !hidden.has(d.id));
    const hiddenList = all.filter((d) => hidden.has(d.id));

    moveItemInArray(visible, event.previousIndex, event.currentIndex);
    // Hidden dashboards have no visible order; keep them after the visible order.
    this.dashboardService.dashboardList.set([...visible, ...hiddenList]);
    this.dashboardService.saveDashboardOrderForCurrentProject();
  }

  addDashboard() {
    this.addDashboardService.open();
  }

  addReport(): void {
    const currentProject = this.projectService.currentProject();
    if (!currentProject) return;

    this.reportService.openCreateReportDialog(currentProject);
  }
}
